import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { TenantAdminAuthProvider } from './auth/TenantAdminAuthContext.jsx';
import { SuperAdminAuthProvider } from './auth/SuperAdminAuthContext.jsx';
import { RequireTenantAdmin } from './auth/RequireTenantAdmin.jsx';
import { RequireSuperAdmin } from './auth/RequireSuperAdmin.jsx';
import { TenantAdminShell } from './components/layout/TenantAdminShell.jsx';
import { SuperAdminShell } from './components/layout/SuperAdminShell.jsx';

import { LoginPage } from './pages/tenantAdmin/LoginPage.jsx';
import { PasskeyLoginPage } from './pages/tenantAdmin/PasskeyLoginPage.jsx';
import { DashboardPage } from './pages/tenantAdmin/DashboardPage.jsx';
import { MenusListPage } from './pages/tenantAdmin/MenusListPage.jsx';
import { MenuEditorPage } from './pages/tenantAdmin/menuEditor/MenuEditorPage.jsx';
import { MenuAppearancePage } from './pages/tenantAdmin/MenuAppearancePage.jsx';
import { MenuPreviewPage } from './pages/tenantAdmin/MenuPreviewPage.jsx';
import { PublishHistoryPage } from './pages/tenantAdmin/PublishHistoryPage.jsx';
import { MediaLibraryPage } from './pages/tenantAdmin/MediaLibraryPage.jsx';
import { SecuritySettingsPage } from './pages/tenantAdmin/SecuritySettingsPage.jsx';
import { ForgotPasswordPage, SetPasswordPage } from './pages/tenantAdmin/EmailActionPages.jsx';

import { LoginPage as SuperAdminLoginPage } from './pages/superAdmin/LoginPage.jsx';
import { TenantsListPage } from './pages/superAdmin/TenantsListPage.jsx';
import { TenantDetailPage } from './pages/superAdmin/TenantDetailPage.jsx';
import { TenantMenuPreviewPage } from './pages/superAdmin/TenantMenuPreviewPage.jsx';

// Each section only mounts its own auth provider — scoped this way (rather
// than both wrapping the whole app) so visiting a tenant-admin URL never
// fires a spurious GET /api/super-admin/auth/me probe, and vice versa.
function TenantAdminSection() {
  return (
    <TenantAdminAuthProvider>
      <Outlet />
    </TenantAdminAuthProvider>
  );
}

function SuperAdminSection() {
  return (
    <SuperAdminAuthProvider>
      <Outlet />
    </SuperAdminAuthProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route element={<TenantAdminSection />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/login/passkey" element={<PasskeyLoginPage />} />
          <Route element={<RequireTenantAdmin />}>
            <Route element={<TenantAdminShell />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/menus" element={<MenusListPage />} />
              <Route path="/menus/:menuId" element={<MenuEditorPage />} />
              <Route path="/menus/:menuId/appearance" element={<MenuAppearancePage />} />
              <Route path="/menus/:menuId/preview" element={<MenuPreviewPage />} />
              <Route path="/menus/:menuId/history" element={<PublishHistoryPage />} />
              <Route path="/media" element={<MediaLibraryPage />} />
              <Route path="/settings/security" element={<SecuritySettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route element={<SuperAdminSection />}>
          <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
          <Route element={<RequireSuperAdmin />}>
            <Route element={<SuperAdminShell />}>
              <Route path="/super-admin/tenants" element={<TenantsListPage />} />
              <Route path="/super-admin/tenants/:tenantId" element={<TenantDetailPage />} />
              <Route path="/super-admin/tenants/:tenantId/menus/:menuId" element={<TenantMenuPreviewPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
